import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Block,
  Heading,
  Paragraph,
  RadioButton,
  RadioButtonGroup,
  ExternalLink,
  StatusText,
} from 'suomifi-ui-components'

import { normalizeTextInputWhitespace } from 'todistukset-common/src/vtj/form-sanitization.util'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'

import {
  FormContainer,
  InfoInlineAlert,
  StyledTextInput,
  TextInputs,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  type FirstErrorImperativeHandle,
  useFirstErrorImperativeHandle,
  getErrorStatus,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useAetOrdererFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-orderer-form-actions'
import {
  AetLastnameSelection,
  useAetOrdererForm,
} from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import {
  validateKeepingCurrentLastName,
  validateNewLastName,
  validateSpouseKeepingCurrentLastName,
  validateSpouseNewLastName,
} from 'todistukset-common/src/vtj/validation'
import { AetOrdererUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  finalEditableFieldValue,
  SailyykoSukunimi,
} from 'todistukset-common/src/vtj/types/editable-field.type'

const AetLastnameChoiceStep: StepComponent = observer(
  forwardRef(function AetLastnameChoiceStep({ titleId }, ref) {
    const { t } = useTranslation()

    const ordererErrorRef = useRef<FirstErrorImperativeHandle>(null)
    const spouseErrorRef = useRef<FirstErrorImperativeHandle>(null)
    useImperativeHandle(
      ref,
      () => ({
        getFirstErrorField: () =>
          ordererErrorRef.current?.getFirstErrorField() ??
          spouseErrorRef.current?.getFirstErrorField() ??
          null,
      }),
      []
    )

    const form = useAetOrdererForm()
    const showNamePartsWarning =
      Math.max(
        countNameParts(
          !finalEditableFieldValue(form.keepingCurrentLastName),
          finalEditableFieldValue(form.newLastName)
        ),
        countNameParts(
          !finalEditableFieldValue(form.spouseKeepingCurrentLastName),
          finalEditableFieldValue(form.spouseNewLastName)
        )
      ) > 2

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetOrdererLastnameChoiceMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOrdererLastnameChoiceMainDescription_paragraphs" />
          <Paragraph>
            <ExternalLink
              href={t('aetOrdererLastnameChoiceMoreInfoLinkHref')}
              labelNewWindow={t('opensInNewWindow')}
              underline="initial"
            >
              <LocalizedString stringKey="aetOrdererLastnameChoiceMoreInfoLinkText" />
            </ExternalLink>
          </Paragraph>
        </Block>
        <Block variant="section">
          <LocalizedParagraphs stringKey="aetOrdererLastnameChoiceNameSelectionDescription_paragraphs" />
          <NameWidget
            mainLabel="aetOrdererLastnameChoiceOwnNameMainLabel"
            keepsNameRadioLabel="aetOrdererLastnameChoiceOwnNameRadioLabel_keep"
            changesNameRadioLabel="aetOrdererLastnameChoiceOwnNameRadioLabel_new"
            nameFieldLabel="aetOrdererLastnameChoiceOwnNewNameLabel"
            radioField={{
              field: 'keepingCurrentLastName',
              setter: 'setOrdererKeepingCurrentLastname',
            }}
            textField={{
              field: 'newLastName',
              setter: 'setOrdererNewLastName',
            }}
            ref={ordererErrorRef}
          />
          <NameWidget
            mainLabel="aetOrdererLastnameChoiceSpouseNameMainLabel"
            keepsNameRadioLabel="aetOrdererLastnameChoiceSpouseNameRadioLabel_keep"
            changesNameRadioLabel="aetOrdererLastnameChoiceSpouseNameRadioLabel_new"
            nameFieldLabel="aetOrdererLastnameChoiceSpouseNewNameLabel"
            radioField={{
              field: 'spouseKeepingCurrentLastName',
              setter: 'setSpouseKeepingCurrentLastname',
            }}
            textField={{
              field: 'spouseNewLastName',
              setter: 'setSpouseNewLastName',
            }}
            ref={spouseErrorRef}
          />
          {showNamePartsWarning && (
            <InfoInlineAlert status="warning">
              <LocalizedParagraphs stringKey="warningLastNameMightHaveTooManyParts_paragraphs" />
            </InfoInlineAlert>
          )}
          <Block /* layout spacer */ mt="xxl" />
        </Block>
      </FormContainer>
    )
  })
)

export default AetLastnameChoiceStep

const NameWidget: React.FC<
  {
    mainLabel: string
    keepsNameRadioLabel: string
    changesNameRadioLabel: string
    nameFieldLabel: string
    radioField: {
      field: 'keepingCurrentLastName' | 'spouseKeepingCurrentLastName'
      setter:
        | 'setOrdererKeepingCurrentLastname'
        | 'setSpouseKeepingCurrentLastname'
    }
    textField: {
      field: 'newLastName' | 'spouseNewLastName'
      setter: 'setOrdererNewLastName' | 'setSpouseNewLastName'
    }
  } & React.RefAttributes<FirstErrorImperativeHandle>
> = observer(
  forwardRef(function NameWidget(
    {
      mainLabel,
      keepsNameRadioLabel,
      changesNameRadioLabel,
      nameFieldLabel,
      radioField,
      textField,
    },
    ref
  ) {
    const { t } = useTranslation()
    const {
      steps: {
        [OrderFlowName.AETTODISTUS_ORDERER]: {
          showStepValidationErrors: showErrors,
        },
      },
    } = useStore()
    const form = useAetOrdererForm()
    const actions = useAetOrdererFormActions()
    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'keepingCurrentLastName',
      'newLastName',
      'spouseKeepingCurrentLastName',
      'spouseNewLastName',
    ])

    const getValue = (keepingCurrentLastName: SailyykoSukunimi | null) => {
      if (
        keepingCurrentLastName === null ||
        keepingCurrentLastName.ilmoitettuArvo === null
      ) {
        return null
      } else if (keepingCurrentLastName.ilmoitettuArvo) {
        return AetLastnameSelection.CURRENT_NAME
      }
      return AetLastnameSelection.NEW_NAME
    }

    const errorStatusParams = {
      form,
      showErrors,
      t,
    }

    return (
      <Block mt="m">
        <RadioButtonGroup
          id={radioField.field}
          data-test-id={radioField.field}
          name={radioField.field}
          labelText={t(mainLabel)}
          value={getValue(form[radioField.field]) ?? undefined}
          onChange={actions[radioField.setter]}
        >
          <RadioButton
            value={AetLastnameSelection.CURRENT_NAME}
            forwardedRef={refs[radioField.field]}
          >
            <LocalizedString stringKey={keepsNameRadioLabel} />
          </RadioButton>
          <RadioButton value={AetLastnameSelection.NEW_NAME}>
            <LocalizedString stringKey={changesNameRadioLabel} />
          </RadioButton>
        </RadioButtonGroup>{' '}
        <RadioButtonError
          {...errorStatus({
            ...errorStatusParams,
            field: radioField.field,
          })}
        />
        {getValue(form[radioField.field]) === AetLastnameSelection.NEW_NAME && (
          <TextInputs>
            <StyledTextInput
              id={textField.field}
              data-test-id={textField.field}
              name={textField.field}
              type="text"
              labelText={t(nameFieldLabel)}
              value={form[textField.field]?.ilmoitettuArvo ?? ''}
              onChange={actions[textField.setter]}
              onBlur={() =>
                actions[textField.setter](
                  normalizeTextInputWhitespace(
                    form[textField.field]?.ilmoitettuArvo ?? ''
                  )
                )
              }
              fullWidth
              visualPlaceholder=""
              {...errorStatus({
                ...errorStatusParams,
                field: textField.field,
              })}
              statusTextAriaLiveMode="polite"
              ref={refs[textField.field]}
            />
          </TextInputs>
        )}
      </Block>
    )
  })
)

function countNameParts(enable: boolean, name: string | null): number {
  if (!enable || typeof name !== 'string') {
    return 0
  }

  return name
    .split(/[\s-]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0).length
}

const RadioButtonError: React.FC<ReturnType<typeof errorStatus>> = ({
  status,
  statusText = '',
}) =>
  status === 'error' ? (
    <StatusText ariaLiveMode="polite" status="error">
      {statusText}
    </StatusText>
  ) : null

function errorStatus(params: {
  field: keyof ReturnType<typeof getErrors>
  form: AetOrdererUiForm
  showErrors: boolean
  t: TFunction
}) {
  return getErrorStatus({
    ...params,
    errors: {
      ...getErrors(params.form),
    },
  })
}

function getErrors(form: AetOrdererUiForm) {
  return {
    keepingCurrentLastName: validateKeepingCurrentLastName(
      finalEditableFieldValue(form.keepingCurrentLastName)
    ),
    newLastName: validateNewLastName(
      finalEditableFieldValue(form.newLastName),
      form
    ),
    spouseKeepingCurrentLastName: validateSpouseKeepingCurrentLastName(
      finalEditableFieldValue(form.spouseKeepingCurrentLastName)
    ),
    spouseNewLastName: validateSpouseNewLastName(
      finalEditableFieldValue(form.spouseNewLastName),
      form
    ),
  }
}
